/**
 * components/object/ObjectInspector.jsx — Iter 47 unified inspector.
 *
 * The directive specified exactly 3 tabs:
 *   TAB 1  Overview  — header + status + quick-create note + issues
 *   TAB 2  Photos    — references existing Iter 35 photo system
 *   TAB 3  Timeline  — newest-first operational memory feed
 *
 * Object-agnostic: any of structure / pin / revision / measurement /
 * utility_watch will work — the underlying API resolves the right
 * collection from the registry.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Activity, Camera, ListChecks, Hash, AlertTriangle, Loader2, GitBranch,
  FileText,
} from "lucide-react";
import {
  fetchSummary, OBJECT_STATUS_LABEL,
} from "@/lib/objectIntel";
import { useAuth } from "@/contexts/AuthContext";
import { inspectorTabsForRole } from "@/lib/permissions";
import ObjectTimeline from "./ObjectTimeline";
import ObjectRevisions from "./ObjectRevisions";
import ObjectAssignmentPicker from "./ObjectAssignmentPicker";
import ObjectDocuments from "./ObjectDocuments";
import ObjectHealthPanel from "./ObjectHealthPanel";
import ObjectRelations from "./ObjectRelations";
import {
  ObjectNoteAdder, ObjectIssuesPanel, ObjectStatusPicker,
} from "./ObjectQuickActions";

const ALL_TABS = [
  { id: "overview", label: "Overview", icon: ListChecks },
  { id: "photos", label: "Photos", icon: Camera },
  { id: "documents", label: "Docs", icon: FileText },
  { id: "revisions", label: "Revisions", icon: GitBranch },
  { id: "timeline", label: "Timeline", icon: Activity },
];

export default function ObjectInspector({
  objectType, objectId, photosSlot, headerSlot,
  initialTab = "overview",
}) {
  const { user } = useAuth();
  // Iter 63 P0-4 — role-aware tabs. Viewer/inspector/safety/subcontractor
  // see Overview · Docs · Photos. Foreman adds Timeline. Operational
  // roles see the full 5-tab set.
  const tabs = useMemo(() => {
    const allowed = new Set(inspectorTabsForRole(user));
    return ALL_TABS.filter((t) => allowed.has(t.id));
  }, [user]);
  const allowedIds = useMemo(() => new Set(tabs.map((t) => t.id)), [tabs]);
  const [tab, setTab] = useState(
    allowedIds.has(initialTab) ? initialTab : (tabs[0]?.id || "overview"),
  );
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);

  // Keep current tab in sync if role changes mid-session or initialTab
  // points at a hidden tab.
  useEffect(() => {
    if (!allowedIds.has(tab)) setTab(tabs[0]?.id || "overview");
  }, [allowedIds, tab, tabs]);

  // Iter 52 — listen for cross-component "switch tab" hints (e.g. a
  // document hit in the CommandPalette jumps to the owning object's
  // Documents tab).
  useEffect(() => {
    const onSwitch = (e) => {
      const t = e.detail || {};
      if (t.object_type === objectType && t.object_id === objectId && t.tab) {
        if (allowedIds.has(t.tab)) setTab(t.tab);
      }
    };
    window.addEventListener("forgedops:inspector-tab", onSwitch);
    return () => window.removeEventListener("forgedops:inspector-tab", onSwitch);
  }, [objectType, objectId, allowedIds]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setSummary(await fetchSummary(objectType, objectId));
    } catch (e) {
      setSummary({ error: e?.message || "summary failed" });
    } finally {
      setLoading(false);
    }
  }, [objectType, objectId]);

  useEffect(() => { reload(); }, [reload, refreshKey]);

  const bump = () => setRefreshKey((k) => k + 1);

  // Iter 67 Phase 3 — Inspector minimize pill.
  const [minimized, setMinimized] = useState(false);
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        data-testid="object-inspector-restore"
        className="absolute top-3 right-3 z-30 inline-flex items-center gap-2 min-h-[44px] px-3 rounded-full bg-[var(--fop-primary-soft)] border border-[var(--fop-primary)] text-[var(--fop-primary)] font-mono text-[10px] uppercase tracking-wider shadow-lg hover:bg-[var(--fop-cta-bg)] hover:text-white transition-colors"
        title="Expand inspector"
      >
        {summary?.label || objectType}
      </button>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="object-inspector">
      {/* Header (directive §SUCCESS — show label + counters) */}
      <ObjectInspectorHeader summary={summary} loading={loading}
                              objectType={objectType} objectId={objectId}
                              onMinimize={() => setMinimized(true)}
                              extra={headerSlot} />

      {/* Tab bar — role-aware (Iter 63 P0-4) */}
      <div className="flex border-b border-[var(--fop-border)] px-2 shrink-0"
            data-testid="object-inspector-tabs">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`object-tab-${t.id}`}
              className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                active
                  ? "text-[var(--fop-primary)] border-[var(--fop-primary)]"
                  : "text-[var(--fop-text-3)] border-transparent hover:text-[var(--fop-text)]"
              }`}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {tab === "overview" && (
          <>
            <ObjectHealthPanel objectType={objectType} objectId={objectId}
                                 refreshKey={refreshKey} />
            <ObjectStatusPicker objectType={objectType} objectId={objectId}
                                 onChanged={bump} />
            <ObjectAssignmentPicker objectType={objectType} objectId={objectId}
                                      onChanged={bump} />
            <div className="fop-surface p-3 rounded-md">
              <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] mb-2">
                Add Note
              </div>
              <ObjectNoteAdder objectType={objectType} objectId={objectId}
                                onAdded={bump} />
            </div>
            <ObjectIssuesPanel objectType={objectType} objectId={objectId}
                                refreshKey={refreshKey} onChanged={bump} />
            {/* R11 (2026-09-10) — was already built (Iter 48) and wired
                into StructureInspector's bespoke tabs, but never reached
                the generic ObjectInspector Overview tab that backs
                alignment/pipe/conflict. Extending coverage here. */}
            <ObjectRelations objectType={objectType} objectId={objectId}
                              refreshKey={refreshKey} />
          </>
        )}
        {tab === "photos" && (
          <div data-testid="object-inspector-photos">
            {photosSlot ?? (
              <div className="text-[var(--fop-text-3)] text-sm text-center py-12">
                Photos render here using the existing Iter 35 photo system
                (mount the project-specific photos component as
                <code className="font-mono text-[10px] mx-1">photosSlot</code>).
              </div>
            )}
          </div>
        )}
        {tab === "documents" && (
          <ObjectDocuments
            objectType={objectType} objectId={objectId}
            onChanged={bump}
          />
        )}
        {tab === "revisions" && (
          <ObjectRevisions objectType={objectType} objectId={objectId}
                            refreshKey={refreshKey} />
        )}
        {tab === "timeline" && (
          <ObjectTimeline objectType={objectType} objectId={objectId}
                           refreshKey={refreshKey} />
        )}
      </div>
    </div>
  );
}

function ObjectInspectorHeader({ summary, loading, objectType, objectId, extra, onMinimize }) {
  if (loading || !summary) {
    return (
      <div className="px-4 py-3 border-b border-[var(--fop-border)] flex items-center gap-2 text-[var(--fop-text-3)] text-xs">
        <Loader2 size={12} className="animate-spin" /> Loading {objectType}…
      </div>
    );
  }
  if (summary.error) {
    return (
      <div className="px-4 py-3 border-b border-[var(--fop-border)] text-[var(--fop-danger)] text-xs flex items-center gap-2">
        <AlertTriangle size={12} /> {summary.error}
      </div>
    );
  }
  return (
    <div className="px-4 py-3 border-b border-[var(--fop-border)] space-y-2"
          data-testid="object-inspector-header">
      <div className="flex items-baseline gap-2">
        <Hash size={14} className="text-[var(--fop-text-4)]" />
        <h2 className="font-display text-lg font-semibold text-[var(--fop-text)] leading-tight"
             data-testid="object-inspector-label">
          {summary.label || `${objectType.toUpperCase()} ${objectId.slice(-6)}`}
        </h2>
        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] ml-auto">
          {objectType}
        </span>
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            data-testid="object-inspector-minimize"
            aria-label="Minimize inspector"
            title="Minimize"
            className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] h-9 w-9 flex items-center justify-center rounded-md hover:bg-[var(--fop-surface-2)] -mr-1"
          >
            <span className="text-[14px] leading-none">⌄</span>
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
        <span data-testid="counter-photos">Photos: <b className="text-[var(--fop-text)] font-semibold">{summary.photos}</b></span>
        <span data-testid="counter-notes">Notes: <b className="text-[var(--fop-text)] font-semibold">{summary.notes}</b></span>
        <span data-testid="counter-documents">Docs: <b className="text-[var(--fop-text)] font-semibold">{summary.documents ?? 0}</b></span>
        <span data-testid="counter-issues">Issues: <b className="text-[var(--fop-text)] font-semibold">{summary.issues_open}</b>{summary.issues_total > summary.issues_open && (
          <span className="opacity-60"> / {summary.issues_total}</span>
        )}</span>
        {summary.status && (
          <span data-testid="counter-status">Status: <b className="text-[var(--fop-primary)] font-semibold">{OBJECT_STATUS_LABEL[summary.status] || summary.status}</b></span>
        )}
        {summary.assigned_to_name && (
          <span data-testid="counter-assigned-to">Assigned: <b className="text-[var(--fop-primary)] font-semibold">{summary.assigned_to_name}</b></span>
        )}
      </div>
      {extra}
    </div>
  );
}
